import { useRef, useState, useEffect, type KeyboardEvent } from "react";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import C from "classnames";

import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { Badge } from "sj-ui-toolkit/src/atlas/components/Badge";

import S from "./DashboardCard.module.scss";

interface AssessmentCardProps {
  title?: string;
  description?: string;
  isLocked?: boolean;
  estimatedTime: string;
  questionType?: string;
  type: string;
  status?: string;
  dueDate?: string;
  assignmentUUID?: string;
  size?: "small" | "medium" | "large";
  expandToContent?: boolean;
  onNavigate?: () => void;
  variation?: "primary" | "todo";
  testId?: string;
}

export const AssessmentCard = ({
  title,
  description,
  isLocked,
  estimatedTime,
  questionType,
  type,
  status,
  dueDate,
  assignmentUUID,
  size,
  expandToContent = false,
  onNavigate,
  variation = "primary",
  testId,
}: AssessmentCardProps) => {
  const { navigateTo } = useSmartNavigation();
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const element = descriptionRef.current;
    if (element) {
      setIsClamped(element.scrollHeight > element.clientHeight);
    }
  }, [description]);

  const startAssessmentText = isLocked
    ? "Assessment Locked"
    : status === "InProgress"
      ? "Continue Assessment"
      : "Start Assessment";

  const navigateToAssessment = () => {
    if (onNavigate) {
      onNavigate();
      return;
    }

    if (!assignmentUUID) return;
    navigateTo(buildRoute("Ka.routes.assignment.route", { assignmentId: assignmentUUID }));
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch (_error) {
      return dateString;
    }
  };

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (isLocked) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigateToAssessment();
    }
  };

  return (
    <div
      className={C(
        S.assessmentCard,
        isLocked && S.isLocked,
        size === "small" && S.sizeSmall,
        expandToContent && S.expandToContent
      )}
      role="button"
      tabIndex={isLocked ? -1 : 0}
      aria-disabled={isLocked || undefined}
      aria-label={`${startAssessmentText}: ${title}`}
      data-testid={testId}
      onClick={isLocked ? undefined : navigateToAssessment}
      onKeyDown={handleCardKeyDown}
    >
      {variation === "todo" && (
        <svg
          className={S.completionBadge}
          width="90"
          height="101"
          viewBox="0 0 90 101"
          fill="none"
          aria-label="To do: Assessment"
          role="img"
          focusable="false"
        >
          <circle cx="61" cy="40" r="60.1286" stroke="#9EA0A2" strokeWidth="1.74286" />
          <circle cx="60.9996" cy="39.9994" r="49.9297" stroke="#9EA0A2" strokeWidth="1.74286" />
          <path
            d="M38.4851 44.0536L49.7456 59.5895L83.5197 20.4121"
            stroke="#9EA0A2"
            strokeWidth="1.74286"
          />
        </svg>
      )}
      <div className={S.assessmentCardHeader}>
        <Tag value={type} severity="warning" rounded={true} />
      </div>
      <div className={S.assessmentCardBody}>
        <h3 className="atlas-h4">{title}</h3>
        <p
          ref={descriptionRef}
          className={C("body-md", S.clampedText)}
          data-pr-tooltip={isClamped ? description : undefined}
          data-pr-position="bottom"
          data-pr-classname={S.descriptionTooltip}
        >
          {description}
        </p>
        {isClamped && <Tooltip target={descriptionRef} style={{ maxWidth: "400px" }} />}
      </div>
      <div className={S.assessmentDetailsWrapper}>
        {dueDate && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Due Date</p>
            <p className="body-md">{formatDate(dueDate)}</p>
          </div>
        )}
        <div>
          <p className={C(S.mutedText, "body-sm")}>Estimated Time</p>
          <p className="body-md">~ {estimatedTime} mins</p>
        </div>
        {questionType && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Question Type</p>
            <p className="body-md">{questionType}</p>
          </div>
        )}
      </div>
      <div className={S.assessmentCardFooter}>
        {/* Visual-only button: the card itself is the interactive control (role="button" on
            outer div). Rendering a real <button> here would nest interactive controls, which
            assistive tech can't reliably announce. */}
        <span className={C("p-button p-component", { "p-disabled": isLocked })} aria-hidden="true">
          <span className="p-button-label p-c">{startAssessmentText}</span>
        </span>
      </div>
      {variation === "primary" && (
        <div className={S.badgeSvgWrapper}>
          <Badge name="assessment" size={90} />
        </div>
      )}
    </div>
  );
};

// Suggested change to contrast on dark mode #525DBF => #8888D3
